import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Field, Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Switch } from "@/components/ui/Switch";

export type ModuleConfigScope = "game" | "instance";

export interface ModuleConfigFormProps {
  rawConfig: string;
  scope: ModuleConfigScope;
  disabled?: boolean;
  footer?: ReactNode;
  onSubmit: (text: string, action?: string) => void;
}

export function ModuleToggle({
  label,
  checked,
  disabled,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded border border-[hsl(var(--border))] p-3 text-sm">
      <span>{label}</span>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
    </label>
  );
}

export function ModuleConfigFields({
  children,
  footer,
  onSubmit,
}: {
  children: ReactNode;
  footer?: ReactNode;
  onSubmit: (action?: string) => void;
}) {
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        const submitter =
          "submitter" in event.nativeEvent
            ? (event.nativeEvent.submitter as HTMLElement | null)
            : null;
        onSubmit(submitter?.dataset.action);
      }}
      noValidate
    >
      {children}
      {footer}
    </form>
  );
}

export function ContinentFleeConfigForm({
  rawConfig,
  scope,
  disabled,
  footer,
  onSubmit,
}: ModuleConfigFormProps) {
  const parsed = useMemo(() => parseContinentFleeDesired(rawConfig), [rawConfig]);
  const [load, setLoad] = useState(parsed.load);
  const [enabled, setEnabled] = useState(parsed.enabled);
  const [nearby, setNearby] = useState(parsed.fleeOnNearbyPlayerEnabled);
  const [revive, setRevive] = useState(parsed.reviveReturnEnabled);
  const [logEnabled, setLogEnabled] = useState(parsed.logEnabled);
  const [boss, setBoss] = useState(parsed.bossEscapeEnabled);
  const [bossSmart, setBossSmart] = useState(parsed.bossSmartEnabled);
  const [bossStone, setBossStone] = useState(parsed.bossReturnStoneName);
  const [channel, setChannel] = useState(parsed.bossReturnStoneChannelEnabled);
  const [selfHp, setSelfHp] = useState(String(parsed.selfHpPercent));
  const [playerHp, setPlayerHp] = useState(String(parsed.playerHardFleeHpRatio));
  const [playerPower, setPlayerPower] = useState(String(parsed.playerCombatPowerRatio));

  useEffect(() => {
    setLoad(parsed.load);
    setEnabled(parsed.enabled);
    setNearby(parsed.fleeOnNearbyPlayerEnabled);
    setRevive(parsed.reviveReturnEnabled);
    setLogEnabled(parsed.logEnabled);
    setBoss(parsed.bossEscapeEnabled);
    setBossSmart(parsed.bossSmartEnabled);
    setBossStone(parsed.bossReturnStoneName);
    setChannel(parsed.bossReturnStoneChannelEnabled);
    setSelfHp(String(parsed.selfHpPercent));
    setPlayerHp(String(parsed.playerHardFleeHpRatio));
    setPlayerPower(String(parsed.playerCombatPowerRatio));
  }, [parsed]);

  return (
    <ModuleConfigFields
      footer={footer}
      onSubmit={(action) =>
        onSubmit(
          buildContinentFleeDesired(parsed.raw, {
            load: scope === "game" ? load : undefined,
            enabled,
            fleeOnNearbyPlayerEnabled: nearby,
            reviveReturnEnabled: revive,
            logEnabled,
            bossEscapeEnabled: boss,
            bossSmartEnabled: bossSmart,
            bossReturnStoneName: bossStone,
            bossReturnStoneChannelEnabled: channel,
            selfHpPercent: selfHp,
            playerHardFleeHpRatio: playerHp,
            playerCombatPowerRatio: playerPower,
          }),
          action,
        )
      }
    >
      {scope === "game" ? (
        <ModuleToggle
          label="加载回城保护模块"
          checked={load}
          disabled={disabled}
          onCheckedChange={setLoad}
        />
      ) : null}
      <div className="grid gap-3 md:grid-cols-2">
        <ModuleToggle
          label={scope === "game" ? "启用回城保护" : "回城保护"}
          checked={enabled}
          disabled={disabled}
          onCheckedChange={setEnabled}
        />
        <ModuleToggle
          label="见人就飞"
          checked={nearby}
          disabled={disabled}
          onCheckedChange={setNearby}
        />
        <ModuleToggle
          label="复活CD回城"
          checked={revive}
          disabled={disabled}
          onCheckedChange={setRevive}
        />
        <ModuleToggle
          label="文件日志"
          checked={logEnabled}
          disabled={disabled}
          onCheckedChange={setLogEnabled}
        />
        <ModuleToggle
          label="BOSS兜底"
          checked={boss}
          disabled={disabled}
          onCheckedChange={setBoss}
        />
        <ModuleToggle
          label="智能BOSS"
          checked={bossSmart}
          disabled={disabled}
          onCheckedChange={setBossSmart}
        />
        <ModuleToggle
          label="读条回城"
          checked={channel}
          disabled={disabled}
          onCheckedChange={setChannel}
        />
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="自身低血(%)">
          <Input
            type="number"
            min={1}
            max={100}
            value={selfHp}
            disabled={disabled}
            onChange={(event) => setSelfHp(event.target.value)}
          />
        </Field>
        <Field label="玩家最大血倍率">
          <Input
            type="number"
            min={0}
            max={100}
            step="0.1"
            value={playerHp}
            disabled={disabled}
            onChange={(event) => setPlayerHp(event.target.value)}
          />
        </Field>
        <Field label="战斗力倍率">
          <Input
            type="number"
            min={0}
            max={100}
            step="0.1"
            value={playerPower}
            disabled={disabled}
            onChange={(event) => setPlayerPower(event.target.value)}
          />
        </Field>
        <Field label="读条回城石">
          <Input
            value={bossStone}
            disabled={disabled}
            onChange={(event) => setBossStone(event.target.value)}
          />
        </Field>
      </div>
    </ModuleConfigFields>
  );
}

export function AutoSummonConfigForm({
  rawConfig,
  scope,
  disabled,
  footer,
  onSubmit,
}: ModuleConfigFormProps) {
  const parsed = useMemo(() => parseAutoSummonDesired(rawConfig), [rawConfig]);
  const [load, setLoad] = useState(parsed.load);
  const [enabled, setEnabled] = useState(parsed.enabled);
  const [mode, setMode] = useState(parsed.mode);
  const [skillID, setSkillID] = useState(String(parsed.skillID));
  const [checkInterval, setCheckInterval] = useState(String(parsed.checkInterval));
  const [cooldown, setCooldown] = useState(String(parsed.cooldown));
  const [failCooldown, setFailCooldown] = useState(String(parsed.failCooldown));
  const [serverMode, setServerMode] = useState(parsed.autoModeRequiresServer);

  useEffect(() => {
    setLoad(parsed.load);
    setEnabled(parsed.enabled);
    setMode(parsed.mode);
    setSkillID(String(parsed.skillID));
    setCheckInterval(String(parsed.checkInterval));
    setCooldown(String(parsed.cooldown));
    setFailCooldown(String(parsed.failCooldown));
    setServerMode(parsed.autoModeRequiresServer);
  }, [parsed]);

  return (
    <ModuleConfigFields
      footer={footer}
      onSubmit={(action) =>
        onSubmit(
          buildAutoSummonDesired(parsed.raw, {
            load: scope === "game" ? load : undefined,
            enabled,
            mode,
            skillID,
            checkInterval,
            cooldown,
            failCooldown,
            autoModeRequiresServer: serverMode,
          }),
          action,
        )
      }
    >
      {scope === "game" ? (
        <ModuleToggle
          label="加载智能召唤模块"
          checked={load}
          disabled={disabled}
          onCheckedChange={setLoad}
        />
      ) : null}
      <ModuleToggle
        label={scope === "game" ? "启用智能召唤" : "智能召唤"}
        checked={enabled}
        disabled={disabled}
        onCheckedChange={setEnabled}
      />
      <Field label="召唤模式">
        <Select
          value={mode}
          disabled={disabled}
          onValueChange={(value) => setMode(value === "auto" ? "auto" : "manual")}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">指定技能</SelectItem>
            <SelectItem value="auto">服务端自动</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="技能 ID">
          <Input
            type="number"
            min={1}
            value={skillID}
            disabled={disabled || mode === "auto"}
            onChange={(event) => setSkillID(event.target.value)}
          />
        </Field>
        <Field label="检测间隔(秒)">
          <Input
            type="number"
            min={0.3}
            max={10}
            step={0.1}
            value={checkInterval}
            disabled={disabled}
            onChange={(event) => setCheckInterval(event.target.value)}
          />
        </Field>
        <Field label="成功冷却(秒)">
          <Input
            type="number"
            min={0.5}
            max={60}
            step={0.5}
            value={cooldown}
            disabled={disabled}
            onChange={(event) => setCooldown(event.target.value)}
          />
        </Field>
        <Field label="失败冷却(秒)">
          <Input
            type="number"
            min={1}
            max={120}
            step={1}
            value={failCooldown}
            disabled={disabled}
            onChange={(event) => setFailCooldown(event.target.value)}
          />
        </Field>
      </div>
      <ModuleToggle
        label="仅服务端开启时自动"
        checked={serverMode}
        disabled={disabled || mode !== "auto"}
        onCheckedChange={setServerMode}
      />
    </ModuleConfigFields>
  );
}

export function AutoSuoYaoConfigForm({
  rawConfig,
  scope,
  disabled,
  footer,
  onSubmit,
}: ModuleConfigFormProps) {
  const parsed = useMemo(() => parseAutoSuoYaoDesired(rawConfig), [rawConfig]);
  const [load, setLoad] = useState(parsed.load);
  const [enterInner, setEnterInner] = useState(parsed.enterInner);

  useEffect(() => {
    setLoad(parsed.load);
    setEnterInner(parsed.enterInner);
  }, [parsed]);

  return (
    <ModuleConfigFields
      footer={footer}
      onSubmit={(action) =>
        onSubmit(
          buildAutoSuoYaoDesired(parsed.raw, {
            load: scope === "game" ? load : undefined,
            enterInner,
          }),
          action,
        )
      }
    >
      {scope === "game" ? (
        <ModuleToggle
          label="加载自动锁妖模块"
          checked={load}
          disabled={disabled}
          onCheckedChange={setLoad}
        />
      ) : null}
      <ModuleToggle
        label="十层后进入内殿"
        checked={enterInner}
        disabled={disabled}
        onCheckedChange={setEnterInner}
      />
    </ModuleConfigFields>
  );
}

export function PullMonsterConfigForm({
  rawConfig,
  scope,
  disabled,
  footer,
  onSubmit,
}: ModuleConfigFormProps) {
  const parsed = useMemo(() => parsePullMonsterDesired(rawConfig), [rawConfig]);
  const [load, setLoad] = useState(parsed.load);
  const [enable, setEnable] = useState(parsed.enable);
  const [minCount, setMinCount] = useState(String(parsed.minCount));
  const [skillID, setSkillID] = useState(String(parsed.skillID));

  useEffect(() => {
    setLoad(parsed.load);
    setEnable(parsed.enable);
    setMinCount(String(parsed.minCount));
    setSkillID(String(parsed.skillID));
  }, [parsed]);

  return (
    <ModuleConfigFields
      footer={footer}
      onSubmit={(action) =>
        onSubmit(
          buildPullMonsterDesired(parsed.raw, {
            load: scope === "game" ? load : undefined,
            enable,
            minCount,
            skillID,
          }),
          action,
        )
      }
    >
      {scope === "game" ? (
        <ModuleToggle
          label="加载全屏吸怪模块"
          checked={load}
          disabled={disabled}
          onCheckedChange={setLoad}
        />
      ) : null}
      <ModuleToggle
        label={scope === "game" ? "启用全屏吸怪" : "全屏吸怪"}
        checked={enable}
        disabled={disabled}
        onCheckedChange={setEnable}
      />
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="怪物只数">
          <Input
            type="number"
            min={1}
            value={minCount}
            disabled={disabled}
            onChange={(event) => setMinCount(event.target.value)}
          />
        </Field>
        <Field label="技能 ID">
          <Input
            type="number"
            min={1}
            value={skillID}
            disabled={disabled}
            onChange={(event) => setSkillID(event.target.value)}
          />
        </Field>
      </div>
    </ModuleConfigFields>
  );
}

export function AutoAuctionBidConfigForm({
  rawConfig,
  scope,
  disabled,
  footer,
  onSubmit,
}: ModuleConfigFormProps) {
  const parsed = useMemo(() => parseAutoAuctionBidDesired(rawConfig), [rawConfig]);
  const [load, setLoad] = useState(parsed.load);
  const [enabled, setEnabled] = useState(parsed.enabled);
  const [maxPrice, setMaxPrice] = useState(String(parsed.maxPrice));
  const [bidStep, setBidStep] = useState(String(parsed.bidStep));
  const [scanInterval, setScanInterval] = useState(String(parsed.scanIntervalSeconds));
  const [minEquipRequiredLevel, setMinEquipRequiredLevel] = useState(
    String(parsed.minEquipRequiredLevel),
  );
  const [currencyNames, setCurrencyNames] = useState(parsed.targetCurrencyNames.join("\n"));
  const [blockedNames, setBlockedNames] = useState(parsed.blockedNameKeywords.join("\n"));

  useEffect(() => {
    setLoad(parsed.load);
    setEnabled(parsed.enabled);
    setMaxPrice(String(parsed.maxPrice));
    setBidStep(String(parsed.bidStep));
    setScanInterval(String(parsed.scanIntervalSeconds));
    setMinEquipRequiredLevel(String(parsed.minEquipRequiredLevel));
    setCurrencyNames(parsed.targetCurrencyNames.join("\n"));
    setBlockedNames(parsed.blockedNameKeywords.join("\n"));
  }, [parsed]);

  return (
    <ModuleConfigFields
      footer={footer}
      onSubmit={(action) =>
        onSubmit(
          buildAutoAuctionBidDesired(parsed.raw, {
            load: scope === "game" ? load : undefined,
            enabled,
            maxPrice,
            bidStep,
            scanIntervalSeconds: scanInterval,
            minEquipRequiredLevel,
            targetCurrencyNames: currencyNames,
            blockedNameKeywords: blockedNames,
          }),
          action,
        )
      }
    >
      {scope === "game" ? (
        <ModuleToggle
          label="加载拍卖竞价模块"
          checked={load}
          disabled={disabled}
          onCheckedChange={setLoad}
        />
      ) : null}
      <ModuleToggle
        label={scope === "game" ? "启用拍卖竞价" : "拍卖竞价"}
        checked={enabled}
        disabled={disabled}
        onCheckedChange={setEnabled}
      />
      <div className="grid gap-3 md:grid-cols-4">
        <Field label="最高出价">
          <Input
            type="number"
            min={1}
            max={100000000}
            value={maxPrice}
            disabled={disabled}
            onChange={(event) => setMaxPrice(event.target.value)}
          />
        </Field>
        <Field label="每次加价">
          <Input
            type="number"
            min={1}
            max={1000000}
            value={bidStep}
            disabled={disabled}
            onChange={(event) => setBidStep(event.target.value)}
          />
        </Field>
        <Field label="扫描间隔(秒)">
          <Input
            type="number"
            min={5}
            max={3600}
            value={scanInterval}
            disabled={disabled}
            onChange={(event) => setScanInterval(event.target.value)}
          />
        </Field>
        <Field label="装备需求等级">
          <Input
            type="number"
            min={0}
            max={100000}
            value={minEquipRequiredLevel}
            disabled={disabled}
            onChange={(event) => setMinEquipRequiredLevel(event.target.value)}
          />
        </Field>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="目标货币">
          <Textarea
            rows={5}
            value={currencyNames}
            disabled={disabled}
            onChange={(event) => setCurrencyNames(event.target.value)}
            spellCheck={false}
          />
        </Field>
        <Field label="名称黑名单">
          <Textarea
            rows={5}
            value={blockedNames}
            disabled={disabled}
            onChange={(event) => setBlockedNames(event.target.value)}
            spellCheck={false}
          />
        </Field>
      </div>
    </ModuleConfigFields>
  );
}

export function parseJSONRecord(raw: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(raw || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return {};
  }
  return {};
}

export function parseAutoMapHuntDesired(raw: string): {
  load: boolean;
  enabled: boolean;
  cutUpgradeEnabled: boolean;
  selectAllMaps: boolean;
  selectedMapIds: string[];
} {
  const parsed = parseJSONRecord(raw);
  const cutUpgrade =
    parsed.cutUpgrade && typeof parsed.cutUpgrade === "object" && !Array.isArray(parsed.cutUpgrade)
      ? (parsed.cutUpgrade as Record<string, unknown>)
      : {};
  return {
    load: parsed.load === undefined ? true : parsed.load === true,
    enabled: parsed.enabled === true,
    cutUpgradeEnabled: cutUpgrade.enabled === undefined ? true : cutUpgrade.enabled === true,
    selectAllMaps: parsed.selectAllMaps === true,
    selectedMapIds: Array.isArray(parsed.selectedMapIds)
      ? parsed.selectedMapIds
          .map((value) => String(value).trim())
          .filter((value) => value.length > 0)
      : [],
  };
}

export function parseContinentFleeDesired(raw: string) {
  const defaults = {
    load: true,
    enabled: true,
    logEnabled: false,
    failureProbeFileEnabled: false,
    fleeOnNearbyPlayerEnabled: true,
    reviveReturnEnabled: true,
    bossEscapeEnabled: true,
    bossSmartEnabled: true,
    bossReturnStoneName: "盟重传送石",
    bossReturnStoneChannelEnabled: true,
    selfHpPercent: 45,
    playerHardFleeHpRatio: 1.3,
    playerCombatPowerRatio: 1.3,
  };
  const parsed = parseJSONRecord(raw);
  const legacyPercent = numberValue(
    parsed.selfHpBelowPlayerPercent ?? parsed.self_hp_below_player_percent,
    75,
    1,
    200,
  );
  const legacyRatio =
    parsed.playerHardFleeHpRatio === undefined && parsed.player_hard_flee_hp_ratio === undefined
      ? legacyPercent === 75
        ? defaults.playerHardFleeHpRatio
        : Number((100 / legacyPercent).toFixed(2))
      : defaults.playerHardFleeHpRatio;
  return {
    raw: parsed,
    load: parsed.load === undefined ? defaults.load : parsed.load === true,
    enabled: parsed.enabled === undefined ? defaults.enabled : parsed.enabled === true,
    logEnabled: parsed.logEnabled === undefined ? defaults.logEnabled : parsed.logEnabled === true,
    failureProbeFileEnabled:
      parsed.failureProbeFileEnabled === undefined
        ? defaults.failureProbeFileEnabled
        : parsed.failureProbeFileEnabled === true,
    fleeOnNearbyPlayerEnabled:
      parsed.fleeOnNearbyPlayerEnabled === undefined
        ? defaults.fleeOnNearbyPlayerEnabled
        : parsed.fleeOnNearbyPlayerEnabled === true,
    reviveReturnEnabled:
      parsed.reviveReturnEnabled === undefined
        ? defaults.reviveReturnEnabled
        : parsed.reviveReturnEnabled === true,
    bossEscapeEnabled:
      parsed.bossEscapeEnabled === undefined
        ? defaults.bossEscapeEnabled
        : parsed.bossEscapeEnabled === true,
    bossSmartEnabled:
      parsed.bossSmartEnabled === undefined
        ? defaults.bossSmartEnabled
        : parsed.bossSmartEnabled === true,
    bossReturnStoneName:
      typeof parsed.bossReturnStoneName === "string"
        ? parsed.bossReturnStoneName
        : defaults.bossReturnStoneName,
    bossReturnStoneChannelEnabled:
      parsed.bossReturnStoneChannelEnabled === undefined
        ? defaults.bossReturnStoneChannelEnabled
        : parsed.bossReturnStoneChannelEnabled === true,
    selfHpPercent: numberValue(parsed.selfHpPercent, defaults.selfHpPercent, 1, 100),
    playerHardFleeHpRatio: numberValue(
      parsed.playerHardFleeHpRatio ?? parsed.player_hard_flee_hp_ratio,
      legacyRatio,
      0,
      100,
    ),
    playerCombatPowerRatio: numberValue(
      parsed.playerCombatPowerRatio ?? parsed.player_combat_power_ratio,
      defaults.playerCombatPowerRatio,
      0,
      100,
    ),
  };
}

export function buildContinentFleeDesired(
  raw: Record<string, unknown>,
  patch: {
    enabled: boolean;
    load?: boolean;
    logEnabled: boolean;
    fleeOnNearbyPlayerEnabled: boolean;
    reviveReturnEnabled: boolean;
    bossEscapeEnabled: boolean;
    bossSmartEnabled: boolean;
    bossReturnStoneName: string;
    bossReturnStoneChannelEnabled: boolean;
    selfHpPercent: string;
    playerHardFleeHpRatio: string;
    playerCombatPowerRatio: string;
  },
) {
  const withoutLegacy = { ...raw };
  delete withoutLegacy.selfHpBelowPlayerPercent;
  delete withoutLegacy.self_hp_below_player_percent;
  delete withoutLegacy.player_hard_flee_hp_ratio;
  delete withoutLegacy.player_combat_power_ratio;
  delete withoutLegacy.quickUseMaxPos;
  delete withoutLegacy.checkInterval;
  delete withoutLegacy.cooldown;
  delete withoutLegacy.useStoneDelay;
  return JSON.stringify(
    stripUndefined({
      ...withoutLegacy,
      load: patch.load,
      enabled: patch.enabled,
      logEnabled: patch.logEnabled,
      fleeOnNearbyPlayerEnabled: patch.fleeOnNearbyPlayerEnabled,
      reviveReturnEnabled: patch.reviveReturnEnabled,
      bossEscapeEnabled: patch.bossEscapeEnabled,
      bossSmartEnabled: patch.bossSmartEnabled,
      bossReturnStoneName: patch.bossReturnStoneName.trim() || "盟重传送石",
      bossReturnStoneChannelEnabled: patch.bossReturnStoneChannelEnabled,
      selfHpPercent: numberValue(patch.selfHpPercent, 45, 1, 100),
      playerHardFleeHpRatio: numberValue(patch.playerHardFleeHpRatio, 1.3, 0, 100),
      playerCombatPowerRatio: numberValue(patch.playerCombatPowerRatio, 1.3, 0, 100),
    }),
    null,
    2,
  );
}

export function parseAutoSummonDesired(raw: string) {
  const defaults = {
    load: true,
    enabled: false,
    mode: "manual",
    skillID: 3001,
    checkInterval: 1,
    cooldown: 3,
    failCooldown: 6,
    autoModeRequiresServer: true,
  };
  const parsed = parseJSONRecord(raw);
  const mode = parsed.mode === "auto" ? "auto" : "manual";
  return {
    raw: parsed,
    load: parsed.load === undefined ? defaults.load : parsed.load === true,
    enabled: parsed.enabled === true,
    mode,
    skillID: numberValue(parsed.skillID, defaults.skillID, 1, 999999),
    checkInterval: numberValue(parsed.checkInterval, defaults.checkInterval, 0.3, 10),
    cooldown: numberValue(parsed.cooldown, defaults.cooldown, 0.5, 60),
    failCooldown: numberValue(parsed.failCooldown, defaults.failCooldown, 1, 120),
    autoModeRequiresServer:
      parsed.autoModeRequiresServer === undefined
        ? defaults.autoModeRequiresServer
        : parsed.autoModeRequiresServer === true,
  };
}

export function buildAutoSummonDesired(
  raw: Record<string, unknown>,
  patch: {
    enabled: boolean;
    load?: boolean;
    mode: string;
    skillID: string;
    checkInterval: string;
    cooldown: string;
    failCooldown: string;
    autoModeRequiresServer: boolean;
  },
) {
  return JSON.stringify(
    stripUndefined({
      ...raw,
      load: patch.load,
      enabled: patch.enabled,
      mode: patch.mode === "auto" ? "auto" : "manual",
      skillID: numberValue(patch.skillID, 3001, 1, 999999),
      checkInterval: numberValue(patch.checkInterval, 1, 0.3, 10),
      cooldown: numberValue(patch.cooldown, 3, 0.5, 60),
      failCooldown: numberValue(patch.failCooldown, 6, 1, 120),
      autoModeRequiresServer: patch.autoModeRequiresServer,
    }),
    null,
    2,
  );
}

export function parseAutoSuoYaoDesired(raw: string) {
  const parsed = parseJSONRecord(raw);
  return {
    raw: parsed,
    load: parsed.load === undefined ? true : parsed.load === true,
    enterInner: parsed.enterInner === true,
  };
}

export function buildAutoSuoYaoDesired(
  raw: Record<string, unknown>,
  patch: { load?: boolean; enterInner: boolean },
) {
  return JSON.stringify(stripUndefined({ ...raw, load: patch.load, enterInner: patch.enterInner }), null, 2);
}

export function parsePullMonsterDesired(raw: string) {
  const defaults = {
    load: true,
    enable: false,
    minCount: 6,
    skillID: 3002,
  };
  const parsed = parseJSONRecord(raw);
  return {
    raw: parsed,
    load: parsed.load === undefined ? defaults.load : parsed.load === true,
    enable: parsed.enable === true,
    minCount: numberValue(parsed.minCount, defaults.minCount, 1, 999),
    skillID: numberValue(parsed.skillID, defaults.skillID, 1, 999999),
  };
}

export function buildPullMonsterDesired(
  raw: Record<string, unknown>,
  patch: { load?: boolean; enable: boolean; minCount: string; skillID: string },
) {
  return JSON.stringify(
    stripUndefined({
      ...raw,
      load: patch.load,
      enable: patch.enable,
      minCount: numberValue(patch.minCount, 6, 1, 999),
      skillID: numberValue(patch.skillID, 3002, 1, 999999),
    }),
    null,
    2,
  );
}

export function parseAutoAuctionBidDesired(raw: string) {
  const defaults = {
    load: true,
    enabled: false,
    maxPrice: 500,
    bidStep: 122,
    scanIntervalSeconds: 3600,
    minEquipRequiredLevel: 0,
    targetCurrencyNames: ["元宝", "积分", "灵符", "金币"],
    blockedNameKeywords: [] as string[],
  };
  const parsed = parseJSONRecord(raw);
  return {
    raw: parsed,
    load: parsed.load === undefined ? defaults.load : parsed.load === true,
    enabled: parsed.enabled === true,
    maxPrice: numberValue(parsed.maxPrice, defaults.maxPrice, 1, 100000000),
    bidStep: numberValue(parsed.bidStep, defaults.bidStep, 1, 1000000),
    scanIntervalSeconds: numberValue(
      parsed.scanIntervalSeconds,
      defaults.scanIntervalSeconds,
      5,
      3600,
    ),
    minEquipRequiredLevel: numberValue(
      parsed.minEquipRequiredLevel ??
        parsed.minEquipmentRequiredLevel ??
        parsed.minRequiredLevel,
      defaults.minEquipRequiredLevel,
      0,
      100000,
    ),
    targetCurrencyNames: stringListValue(
      parsed.targetCurrencyNames ?? parsed.currencyNames,
      defaults.targetCurrencyNames,
    ),
    blockedNameKeywords: stringListValue(
      parsed.blockedNameKeywords ?? parsed.nameBlacklist ?? parsed.blockedItemNames,
      defaults.blockedNameKeywords,
    ),
  };
}

export function buildAutoAuctionBidDesired(
  raw: Record<string, unknown>,
  patch: {
    load?: boolean;
    enabled: boolean;
    maxPrice: string;
    bidStep: string;
    scanIntervalSeconds: string;
    minEquipRequiredLevel: string;
    targetCurrencyNames: string;
    blockedNameKeywords: string;
  },
) {
  const withoutLegacy = { ...raw };
  delete withoutLegacy.currencyNames;
  delete withoutLegacy.nameBlacklist;
  delete withoutLegacy.blockedItemNames;
  delete withoutLegacy.minEquipmentRequiredLevel;
  delete withoutLegacy.minRequiredLevel;
  return JSON.stringify(
    stripUndefined({
      ...withoutLegacy,
      load: patch.load,
      enabled: patch.enabled,
      maxPrice: numberValue(patch.maxPrice, 500, 1, 100000000),
      bidStep: numberValue(patch.bidStep, 122, 1, 1000000),
      scanIntervalSeconds: numberValue(patch.scanIntervalSeconds, 3600, 5, 3600),
      minEquipRequiredLevel: numberValue(patch.minEquipRequiredLevel, 0, 0, 100000),
      targetCurrencyNames: linesValue(patch.targetCurrencyNames, ["元宝", "积分", "灵符", "金币"]),
      blockedNameKeywords: linesValue(patch.blockedNameKeywords, []),
    }),
    null,
    2,
  );
}

function stripUndefined(value: Record<string, unknown>) {
  const next: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    if (item !== undefined) next[key] = item;
  }
  return next;
}

function numberValue(value: unknown, fallback: number, min: number, max: number) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, num));
}

function stringListValue(value: unknown, fallback: string[]) {
  if (!Array.isArray(value)) return fallback;
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of value) {
    const text = String(item ?? "").trim();
    if (text && !seen.has(text)) {
      seen.add(text);
      result.push(text);
    }
  }
  return result.length > 0 ? result : fallback;
}

function linesValue(value: string, fallback: string[]) {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const line of value.split(/\r?\n/)) {
    const text = line.trim();
    if (text && !seen.has(text)) {
      seen.add(text);
      result.push(text);
    }
  }
  return result.length > 0 ? result : fallback;
}
